import axios from 'axios';
import { API_ROUTES } from './routes';
import { getDeviceHeaders, initDevice } from '../utils/deviceId';
import { translate } from '../i18n';

// Storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
};

/**
 * Extract a user-friendly error message from API error responses
 * Handles FastAPI validation errors (422), auth errors (401), and general errors
 */
export const extractErrorMessage = (error) => {
  // Network error
  if (!error.response) {
    return translate('Unable to connect to the server. Please check your internet connection.');
  }

  const { status, data } = error.response;

  // FastAPI validation error (422)
  if (status === 422 && data?.detail) {
    if (Array.isArray(data.detail)) {
      // Extract first validation error message
      const firstError = data.detail[0];
      if (firstError?.msg) {
        // Format field name nicely
        const field = firstError.loc?.slice(-1)[0] || 'field';
        const fieldName = field.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
        return `${fieldName}: ${firstError.msg}`;
      }
    }
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    return translate('Please check your input and try again.');
  }

  // Auth error (401)
  if (status === 401) {
    if (data?.detail) {
      return data.detail;
    }
    return translate('Invalid email or password.');
  }

  // Conflict error (409) - e.g., email already exists
  if (status === 409) {
    if (data?.detail) {
      return data.detail;
    }
    return translate('This email is already registered. Please sign in instead.');
  }

  // Forbidden (403)
  if (status === 403) {
    if (data?.detail) {
      return data.detail;
    }
    return translate('You do not have permission to perform this action.');
  }

  // Server error (500+)
  if (status >= 500) {
    return translate('Something went wrong on our end. Please try again later.');
  }

  // Generic error with detail
  if (data?.detail) {
    return data.detail;
  }

  // Fallback
  return translate('An unexpected error occurred. Please try again.');
};

// Token helpers
export const getAccessToken = () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
export const getRefreshToken = () => localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

export const setAuthTokens = (accessToken, refreshToken) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
};

export const clearAuthTokens = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
};

// Create axios instance
export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Device id + fingerprint for fraud prevention
    await initDevice();
    Object.assign(config.headers, getDeviceHeaders());
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh on 401 and suspended accounts on 403
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check for account suspension (403 with suspended message)
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (typeof detail === 'string') {
        if (detail.toLowerCase().includes('suspended') || detail.toLowerCase().includes('unusual activity')) {
          // Dispatch a global event so AuthContext can show the suspended modal
          window.dispatchEvent(new CustomEvent('account-suspended'));
          return Promise.reject(error);
        }
        if (detail.toLowerCase().includes('verification') || detail.toLowerCase().includes('verify')) {
          // Dispatch a global event for verification required
          window.dispatchEvent(new CustomEvent('verification-required'));
          return Promise.reject(error);
        }
      }
    }

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Try to refresh the token
        const response = await axios.post(API_ROUTES.AUTH.REFRESH, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Store new tokens
        setAuthTokens(access_token, newRefreshToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        clearAuthTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
